import { createApi } from '@reduxjs/toolkit/query/react'
// import { User, CreateUserReq, UpdateUserReq, ListUserResp, OkResp } from '../types/models/user'
import baseQueryWithReauth from './baseQuery'
import { apiTags } from './apiTags'
import { CreateUserReq, ListUserResp, UpdateUserReq } from '../types/api/api-user'
import { OkResp } from '../types/api/responses'
import { User } from '../types/models/user'

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [apiTags.User, apiTags.UserList, apiTags.UserProfile, apiTags.UserVerification],
  endpoints: (builder) => ({
    createUser: builder.mutation<User, CreateUserReq>({
      query: (userData) => ({
        url: 'users',
        method: 'POST',
        body: userData,
      }),
      invalidatesTags: (result) => [
        { type: apiTags.UserList, id: 'LIST' },
        ...(result?.id ? [{ type: apiTags.User, id: result.id }] : []),
      ],
    }),

    getUsers: builder.query<
      ListUserResp,
      {
        limit?: number
        page?: number
        sort_key?: string
        sort_val?: number
      }
    >({
      query: (params) => ({
        url: 'users',
        params,
      }),
      providesTags: (result) => [
        { type: apiTags.UserList, id: 'LIST' },
        ...(result?.users?.map(({ id }: { id: string }) => ({
          type: apiTags.User,
          id,
        })) || []),
      ],
    }),

    getUsersByOrganization: builder.query<ListUserResp, void>({
      query: () => 'users/org',
      providesTags: (result) => [
        { type: apiTags.UserList, id: 'ORG' },
        ...(result?.users?.map(({ id }: { id: string }) => ({
          type: apiTags.User,
          id,
        })) || []),
      ],
    }),

    getUserById: builder.query<User, string>({
      query: (id) => `users/${id}`,
      providesTags: (result, error, id) => [
        { type: apiTags.User, id },
        { type: apiTags.UserProfile, id },
      ],
    }),

    deleteUser: builder.mutation<OkResp, string>({
      query: (id) => ({
        url: `users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: apiTags.User, id },
        { type: apiTags.UserList, id: 'LIST' },
        { type: apiTags.UserList, id: 'ORG' },
        { type: apiTags.UserProfile, id },
        { type: apiTags.UserVerification, id },
      ],
    }),

    updateUser: builder.mutation<User, { id: string; userData: UpdateUserReq }>({
      query: ({ id, userData }) => ({
        url: `users/${id}`,
        method: 'PATCH',
        body: userData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.User, id },
        { type: apiTags.UserList, id: 'LIST' },
        { type: apiTags.UserList, id: 'ORG' },
      ],
    }),

    updateUserProfile: builder.mutation<OkResp, { id: string; file: File }>({
      query: ({ id, file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: `users/${id}/profile`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: apiTags.UserProfile, id },
        { type: apiTags.UserList, id: 'LIST' },
        { type: apiTags.UserList, id: 'ORG' },
      ],
    }),

    deleteUserProfile: builder.mutation<OkResp, string>({
      query: (id) => ({
        url: `users/${id}/profile`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: apiTags.UserProfile, id },
        { type: apiTags.UserList, id: 'LIST' },
        { type: apiTags.UserList, id: 'ORG' },
      ],
    }),

    getUserEmailVerificationCode: builder.query<OkResp, { id: string; email: string }>({
      query: ({ id, email }) => ({
        url: `users/${id}/email-verification`,
        params: { email },
      }),
      providesTags: (result, error, { id }) => [{ type: apiTags.UserVerification, id }],
    }),

    confirmUserEmailVerificationCode: builder.query<
      OkResp,
      { id: string; email: string; code: string }
    >({
      query: ({ id, email, code }) => ({
        url: `users/${id}/email-verification-confirm`,
        params: { email, code },
      }),
      providesTags: (result, error, { id }) => [{ type: apiTags.UserVerification, id }],
    }),
  }),
})

export const {
  useCreateUserMutation,
  useGetUsersQuery,
  useGetUsersByOrganizationQuery,
  useGetUserByIdQuery,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
  useDeleteUserProfileMutation,
  useGetUserEmailVerificationCodeQuery,
  useConfirmUserEmailVerificationCodeQuery,
} = userApi
